import { Helmet } from 'react-helmet-async';

import type { RiftboundCard, RiftboundFilter } from '../types';
import { SITE_NAME, SITE_ORIGIN, SITE_SEO_KEYWORDS } from '../constants/site';
import { SET_CODE_TO_SLUG } from '../constants';
import { hasActiveFilter } from '../utils/cardUtils';

interface CardSnippetForLd {
  name: string;
  description?: string;
  image?: string;
  /** Path-only URL e.g. `/cards/xyz` — combined with SITE_ORIGIN in JSON-LD */
  urlPath: string;
  setCode?: string;
  setName?: string;
  /** Official Korean/Chinese names. Same CreativeWork; not a second URL. */
  alternateNames?: string[];
}

interface HowToStep {
  name: string;
  text: string;
}

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  /** Absolute URL, or root-relative path (will be prefixed with SITE_ORIGIN for OG tags). */
  image?: string;
  /** Absolute URL, or root-relative path; used for canonical + OG url. */
  url?: string;
  searchTerm?: string;
  filter?: RiftboundFilter;
  totalResults?: number;
  /** When a card modal is open, prefer card-specific title/description/image. */
  focusedCard?: RiftboundCard | null;
  /** Explicit JSON-LD for a card detail route (path should match location pathname). */
  cardSnippet?: CardSnippetForLd;
  /** JSON-LD for a public/deck snapshot page — shared deck listings. */
  deckSnippet?: {
    name: string;
    description: string;
    urlPath: string;
    image?: string;
    authorDisplayName?: string;
    authorHandle?: string;
  };
  /** Alters inferred description intro (search vs collection views). */
  listingVariant?: 'search' | 'collection';
  /** Prevents indexing for private/auth/account pages while preserving normal SEO defaults elsewhere. */
  noindex?: boolean;
  nofollow?: boolean;
  /**
   * Emit `WebSite + SearchAction` and `Organization` JSON-LD blocks.
   * Set to `true` on the home page only.
   */
  siteSchema?: boolean;
  /** HowTo rich result. Used by the proxy printer. */
  howTo?: {
    name: string;
    description: string;
    steps: HowToStep[];
  };
}

function resolveAbsoluteUrl(href: string): string {
  if (href.startsWith('http')) return href;
  const path = href.startsWith('/') ? href : `/${href}`;
  return `${SITE_ORIGIN}${path}`;
}

function resolveOgImage(image: string | undefined): string {
  if (!image) return `${SITE_ORIGIN}/og-image.png`;
  return resolveAbsoluteUrl(image);
}

function resolveSetBreadcrumbUrl(setCode: string | undefined): string {
  if (!setCode) return `${SITE_ORIGIN}/search`;
  const setSlug = SET_CODE_TO_SLUG[setCode.toUpperCase()];
  if (setSlug) return `${SITE_ORIGIN}/sets/${setSlug}`;
  return `${SITE_ORIGIN}/search?set=${encodeURIComponent(setCode)}`;
}

function filterSummary(filter: RiftboundFilter | undefined): string[] {
  if (!filter) return [];
  const parts: string[] = [];
  if (filter.cardType?.length) parts.push(`${filter.cardType.length} card type(s)`);
  if (filter.region?.length) parts.push(`${filter.region.length} region(s)`);
  if (filter.champion?.length) parts.push(`${filter.champion.length} champion(s)`);
  if (filter.keyword?.length) parts.push(`${filter.keyword.length} keyword(s)`);
  if (filter.set?.length) parts.push(`${filter.set.length} set(s)`);
  if (filter.rarity?.length) parts.push(`${filter.rarity.length} rarities`);
  if (filter.domain?.length) parts.push(`${filter.domain.length} domain(s)`);
  if (filter.artist?.length) parts.push(`${filter.artist.length} artist(s)`);
  if (filter.cost?.length) parts.push('cost filters');
  if (filter.might?.length) parts.push('might filters');
  if (filter.power?.length) parts.push('power filters');
  if (filter.ownedOnly) parts.push('owned only');
  if (filter.includePreviewed === false) parts.push('hide previewed');
  return parts;
}

function filterKeywordExtras(filter: RiftboundFilter | undefined): string[] {
  if (!filter) return [];
  return [
    ...(filter.cardType ?? []),
    ...(filter.region ?? []),
    ...(filter.champion ?? []),
    ...(filter.keyword ?? []),
    ...(filter.set ?? []),
    ...(filter.rarity ?? []),
    ...(filter.domain ?? []),
    ...(filter.artist ?? []),
  ];
}

export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  searchTerm,
  filter,
  totalResults,
  focusedCard,
  cardSnippet,
  deckSnippet,
  listingVariant = 'search',
  noindex = false,
  nofollow,
  siteSchema = false,
  howTo,
}: SEOProps) {
  const effectiveFilter = filter;

  const listingIntro =
    listingVariant === 'collection'
      ? `Browse and filter your Riot Riftbound TCG collection on ${SITE_NAME}. `
      : `Search and filter every Riot Riftbound TCG card on ${SITE_NAME}. `;

  const generateTitle = () => {
    if (title) return title;
    if (focusedCard?.name) {
      const setLabel = focusedCard.setName || focusedCard.setCode;
      const head = setLabel ? `${focusedCard.name} (${setLabel})` : focusedCard.name;
      return `${head} · Riftbound TCG card · ${SITE_NAME}`;
    }
    if (listingVariant === 'collection') {
      if (searchTerm?.trim()) {
        return `Riftbound TCG collection: "${searchTerm.trim()}" · ${SITE_NAME}`;
      }
      if (effectiveFilter && hasActiveFilter(effectiveFilter, '')) {
        return `Riftbound TCG collection (filtered) · ${SITE_NAME}`;
      }
      return `Riftbound TCG collection tracker · ${SITE_NAME}`;
    }
    if (searchTerm?.trim()) {
      return `Riftbound TCG search: "${searchTerm.trim()}" · ${SITE_NAME}`;
    }
    if (effectiveFilter && hasActiveFilter(effectiveFilter, '')) {
      return `Riftbound TCG cards (filtered) · ${SITE_NAME}`;
    }
    return `Riftbound Card Search & Card Database | ${SITE_NAME}`;
  };

  const generateDescription = () => {
    if (description) return description;
    if (focusedCard?.name) {
      const bits = [
        focusedCard.cardType,
        focusedCard.rarity,
        focusedCard.region || focusedCard.regions?.join(', '),
      ].filter(Boolean);
      const head = bits.length ? `${bits.join(' · ')}. ` : '';
      const body = (focusedCard.text || '').trim();
      const snippet = body.length > 200 ? `${body.slice(0, 197)}…` : body;
      return `${head}${snippet || `Riftbound TCG card in ${SITE_NAME}.`}`.trim();
    }
    let desc = listingIntro;
    if (searchTerm?.trim()) {
      desc += `Showing results for "${searchTerm.trim()}". `;
    }
    const parts = filterSummary(effectiveFilter);
    if (parts.length) {
      desc += `Filtered by: ${parts.join(', ')}. `;
    }
    if (totalResults !== undefined) {
      desc += `Found ${totalResults} card${totalResults !== 1 ? 's' : ''}. `;
    }
    desc +=
      listingVariant === 'collection'
        ? 'Export CSV or Excel, spot missing playsets, and sync quantities from Search or Card Proxy.'
        : 'Use official Riftbound API data: search cards, build and export decks, print proxies.';
    return desc;
  };

  const generateKeywords = () => {
    if (keywords) return keywords;
    let base = SITE_SEO_KEYWORDS;
    if (searchTerm?.trim()) {
      base = `${searchTerm.trim()}, ${base}`;
    }
    const extras = filterKeywordExtras(effectiveFilter);
    if (extras.length) {
      base = `${extras.join(', ')}, ${base}`;
    }
    if (focusedCard?.name) {
      const kk = focusedCard.keywords?.filter(Boolean).join(', ');
      base = kk ? `${focusedCard.name}, ${kk}, ${base}` : `${focusedCard.name}, ${base}`;
    }
    return base;
  };

  const getCanonicalUrl = () => {
    if (url) return resolveAbsoluteUrl(url);
    if (typeof window !== 'undefined') {
      const { pathname } = window.location;
      return `${SITE_ORIGIN}${pathname}`;
    }
    return `${SITE_ORIGIN}/`;
  };

  const currentTitle = generateTitle();
  const currentDescription = generateDescription();
  const currentKeywords = generateKeywords();
  const currentUrl = getCanonicalUrl();
  const ogImage = resolveOgImage(
    deckSnippet?.image ||
      focusedCard?.thumbnailUrl ||
      focusedCard?.imageUrl ||
      cardSnippet?.image ||
      image,
  );

  const featuredName = deckSnippet?.name ?? focusedCard?.name ?? cardSnippet?.name;
  const imageAlt = featuredName
    ? `${featuredName} — Riftbound TCG`
    : `${SITE_NAME} — Riftbound TCG companion`;

  const explicitWebPageSchema = title !== undefined && description !== undefined && url !== undefined;
  const shouldNofollow = nofollow ?? noindex;
  const robotsContent = `${noindex ? 'noindex' : 'index'}, ${
    shouldNofollow ? 'nofollow' : 'follow'
  }`;

  const showSearchResultsLd =
    !cardSnippet &&
    (Boolean(searchTerm?.trim()) || Boolean(effectiveFilter && hasActiveFilter(effectiveFilter, '')));

  return (
    <Helmet>
      <title>{currentTitle}</title>
      <meta name="title" content={currentTitle} />
      <meta name="description" content={currentDescription} />
      <meta name="keywords" content={currentKeywords} />
      <meta name="robots" content={robotsContent} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:description" content={currentDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={currentDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      <link rel="canonical" href={currentUrl} />

      {explicitWebPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: currentTitle,
            description: currentDescription,
            url: resolveAbsoluteUrl(url as string),
            isPartOf: {
              '@type': 'WebSite',
              name: SITE_NAME,
              url: `${SITE_ORIGIN}/`,
            },
          })}
        </script>
      )}

      {showSearchResultsLd && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            name: currentTitle,
            description: currentDescription,
            url: currentUrl,
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: totalResults ?? 0,
              itemListElement: [],
            },
          })}
        </script>
      )}

      {cardSnippet && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: cardSnippet.name,
            ...(cardSnippet.alternateNames?.length
              ? { alternateName: cardSnippet.alternateNames }
              : {}),
            description:
              cardSnippet.description ||
              `${cardSnippet.name} — Riftbound TCG card in ${SITE_NAME}.`,
            image: cardSnippet.image ? resolveAbsoluteUrl(cardSnippet.image) : ogImage,
            url: resolveAbsoluteUrl(cardSnippet.urlPath),
            genre: 'Trading Card Game',
            about: {
              '@type': 'Game',
              name: 'Riftbound',
            },
            isPartOf: {
              '@type': 'WebSite',
              name: SITE_NAME,
              url: `${SITE_ORIGIN}/`,
            },
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: SITE_ORIGIN,
            },
          })}
        </script>
      )}

      {cardSnippet && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: SITE_NAME,
                item: `${SITE_ORIGIN}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: cardSnippet.setName || cardSnippet.setCode || 'Cards',
                item: resolveSetBreadcrumbUrl(cardSnippet.setCode),
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: cardSnippet.name,
                item: resolveAbsoluteUrl(cardSnippet.urlPath),
              },
            ],
          })}
        </script>
      )}

      {deckSnippet && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: deckSnippet.name,
            description: deckSnippet.description,
            genre: 'Trading Card Game',
            about: {
              '@type': 'Game',
              name: 'Riftbound',
            },
            url: resolveAbsoluteUrl(deckSnippet.urlPath),
            ...(deckSnippet.image ? { image: resolveAbsoluteUrl(deckSnippet.image) } : {}),
            ...(deckSnippet.authorHandle?.trim() || deckSnippet.authorDisplayName?.trim()
              ? {
                  author: {
                    '@type': 'Person',
                    name:
                      deckSnippet.authorDisplayName?.trim() ||
                      `@${deckSnippet.authorHandle?.trim()}`,
                    ...(deckSnippet.authorHandle?.trim()
                      ? { alternateName: `@${deckSnippet.authorHandle.trim()}` }
                      : {}),
                  },
                }
              : {}),
            isPartOf: {
              '@type': 'WebSite',
              name: SITE_NAME,
              url: `${SITE_ORIGIN}/`,
            },
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: SITE_ORIGIN,
            },
          })}
        </script>
      )}

      {siteSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${SITE_ORIGIN}/search?q={search_term_string}`,
              },
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>
      )}

      {siteSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            logo: `${SITE_ORIGIN}/riftbound-db-logo.png`,
            description:
              'Fan-built Riftbound TCG companion: search cards, track your collection, build decks, and print proxies.',
            sameAs: ['https://discord.gg/HNYv6agPaM'],
          })}
        </script>
      )}

      {howTo && howTo.steps.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: howTo.name,
            description: howTo.description,
            step: howTo.steps.map((step, index) => ({
              '@type': 'HowToStep',
              position: index + 1,
              name: step.name,
              text: step.text,
            })),
          })}
        </script>
      )}
    </Helmet>
  );
}
